import { Injectable } from '@angular/core';
import { InventoryItemDTO } from 'src/app/models/results/inventory-item-dto';
import { InventoryCheckboxFilterModel } from 'src/app/models/results/inventory-checkbox-filter';
import { InventoryGroup, InventoryGroupName } from 'src/app/models/results/inventory-group';
import { InventoryQueryParams } from 'src/app/models/results/inventory-query-params';
import { SharedFilteringService } from '../shared-filtering.service';
import { DataFilteringService } from 'src/app/models/interfaces/data-filtering-service';
import { ConfigService } from '../../config/config.service';
import { EducationInventoryQueryParamsService } from './education-inventory-query-params.service';
import { PriceSort } from 'src/app/models/results/price-sort';

@Injectable({
  providedIn: 'root'
})
export class EducationFilteringService implements DataFilteringService {
  readonly NUMBER_OF_ROUNDS: number = 1;

  constructor(private sharedFilteringService: SharedFilteringService, private confSvc: ConfigService, private educationQueryParamsService: EducationInventoryQueryParamsService) { }

  sortItems(items: InventoryItemDTO[], queryParams: InventoryQueryParams, priceSort?: PriceSort): InventoryItemDTO[] {
    let advancedUseDevices: InventoryItemDTO[] = [];
    let learningAnywhereDevices: InventoryItemDTO[] = [];
    let basicClassroomUseDevices: InventoryItemDTO[] = [];

    const secondaryFormFactors = [this.confSvc.getString("personalComputer"), this.confSvc.getString("chromebox")];
    const groups = this.educationQueryParamsService.getGroups();
    const advancedUseGroup = groups.find(group => group.name === InventoryGroupName.AdvancedUse);
    const learningAnywhereGroup = groups.find(group => group.name === InventoryGroupName.LearningAnywhere);
    const highestGroupForSelectedNeedToRunOptions = queryParams.groups[0];

    items.forEach(item => {
      if (this.deviceIsInGroup(item, advancedUseGroup!)) {
        advancedUseDevices.push(item);
      } else if (this.deviceIsInGroup(item, learningAnywhereGroup!)) {
        learningAnywhereDevices.push(item);
      } else {
        basicClassroomUseDevices.push(item);
      }
    });

    const sortedAdvancedUseDevices = this.getDevicesSortedByPrimaryAndSecondary(advancedUseDevices, secondaryFormFactors, priceSort);
    const sortedLearningAnywhereDevices = this.getDevicesSortedByPrimaryAndSecondary(learningAnywhereDevices, secondaryFormFactors, priceSort);
    const sortedBasicClassroomUseDevices = this.getDevicesSortedByPrimaryAndSecondary(basicClassroomUseDevices, secondaryFormFactors, priceSort);

    if (highestGroupForSelectedNeedToRunOptions.name === InventoryGroupName.AdvancedUse) {
      return sortedAdvancedUseDevices;

    } else if (highestGroupForSelectedNeedToRunOptions.name === InventoryGroupName.LearningAnywhere) {
      return [...sortedLearningAnywhereDevices,
              ...sortedAdvancedUseDevices];

    } else {
      return [...sortedBasicClassroomUseDevices,
              ...sortedLearningAnywhereDevices,
              ...sortedAdvancedUseDevices];
    }
  }

  deviceIsInGroup(item: InventoryItemDTO, group: InventoryGroup): boolean {
    return (item.memorySize >= group.minimumRAM) &&
           (this.sharedFilteringService.inventoryItemContainsAllowedCPU(item, group.allowedCPUs));
  }

  getDevicesSortedByPrimaryAndSecondary(items: InventoryItemDTO[], secondaryFormFactors: string[], priceSort?: PriceSort): InventoryItemDTO[] {
    return [...this.getSortedPrimaryDevices(items, secondaryFormFactors, priceSort), ...this.getSortedSecondaryDevices(items, secondaryFormFactors, priceSort)];
  }

  getSortedPrimaryDevices(items: InventoryItemDTO[], secondaryFormFactors: string[], priceSort?: PriceSort): InventoryItemDTO[] {
    let primaryDevices = items.filter(item => !this.sharedFilteringService.itemProductTypeHasOneOfFormFactors(item, secondaryFormFactors));
    return this.sortByPrice(primaryDevices, priceSort);
  }

  getSortedSecondaryDevices(items: InventoryItemDTO[], secondaryFormFactors: string[], priceSort?: PriceSort): InventoryItemDTO[] {
    let secondaryDevices = items.filter(item => this.sharedFilteringService.itemProductTypeHasOneOfFormFactors(item, secondaryFormFactors));
    return this.sortByPrice(secondaryDevices, priceSort);
  }

  sortByPrice(items: InventoryItemDTO[], priceSort?: PriceSort): InventoryItemDTO[] {
    if (priceSort) {
      return this.sharedFilteringService.sortByPriceSortDirection(items, priceSort);
    }

    return this.sharedFilteringService.sortByPriceLowToHigh(items);
  }

  getRoundCount(): number {
    return this.NUMBER_OF_ROUNDS;
  }

  applyFilteringByRound(
    itemsToFilter: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    group: InventoryGroup,
    round: number,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
      switch (round) {
        case 1:
          return this.applyRound1Filtering(itemsToFilter, queryParams, group, filterItemsFromCheckboxParams);
        default:
          return itemsToFilter;
      }
  }

  applyRound1Filtering(
    itemsToFilter: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    group: InventoryGroup,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
      itemsToFilter = this.sharedFilteringService.filterByBrand(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByCPU(itemsToFilter, group);
      itemsToFilter = this.sharedFilteringService.filterByMemory(itemsToFilter, group);
      itemsToFilter = this.sharedFilteringService.filterByScreenSizeCheckboxes(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByStorageCheckboxes(itemsToFilter, queryParams, group, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByFormFactorCheckboxes(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByFeatureCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByAvailabilityCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByChromebookPlusCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByOsTypeCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);

      return itemsToFilter;
  }
}
